import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ReferenceColorValues() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎨 CSS Reference: Color Values</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS supports multiple <b>color value types</b>. You can define colors by
        name, HEX code, RGB, HSL, HWB, LAB, LCH, or special keywords. These
        allow precise control over design, transparency, and accessibility.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#166534" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <b>Named colors</b>: 140+ predefined keywords.
          </li>
          <li>
            <b>HEX</b>: <code>#RRGGBB</code> or <code>#RGB</code>.
          </li>
          <li>
            <b>RGB & RGBA</b>: 0–255 values with optional alpha.
          </li>
          <li>
            <b>HSL & HSLA</b>: Hue, Saturation, Lightness + alpha.
          </li>
          <li>
            <b>HWB</b>: Hue, Whiteness, Blackness (modern).
          </li>
          <li>
            <b>LAB & LCH</b>: Advanced color spaces (CSS Color 4).
          </li>
          <li>
            <b>Special keywords</b>: <code>currentColor</code>,{" "}
            <code>transparent</code>.
          </li>
        </ul>
      </div>

      {/* Example 1 - Named Colors */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Named Colors</h2>
        <p>
          CSS includes 140+ predefined color names like <code>red</code>,{" "}
          <code>blue</code>, <code>orange</code>.
        </p>
        <CodeCopyBlock
          code={`p { color: red; }
p { color: orange; }
p { color: blue; }`}
        />
        <p style={{ fontSize: "26px", color: "red" }}>This is red</p>
        <p style={{ fontSize: "26px", color: "orange" }}>This is orange</p>
        <p style={{ fontSize: "26px", color: "blue" }}>This is blue</p>
      </div>

      {/* Example 2 - HEX */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ HEX Values</h2>
        <p>
          HEX uses <code>#RRGGBB</code> or shorthand <code>#RGB</code>.
        </p>
        <CodeCopyBlock
          code={`h1 { color: #ff0000; }  /* Red */
h2 { color: #0f0; }     /* Green shorthand (#00ff00) */`}
        />
        <p style={{ fontSize: "26px", color: "#ff0000" }}>#ff0000 (Red)</p>
        <p style={{ fontSize: "26px", color: "#0f0" }}>
          #0f0 (Green shorthand)
        </p>
      </div>

      {/* Example 3 - RGB & RGBA */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ RGB & RGBA</h2>
        <p>
          RGB defines red, green, blue (0–255). RGBA adds transparency (0–1).
        </p>
        <CodeCopyBlock
          code={`div { background: rgb(255, 0, 0); } /* Red */
div { background: rgba(255, 0, 0, 0.5); } /* 50% transparent red */`}
        />
        <div
          style={{
            width: "140px",
            height: "60px",
            background: "rgb(255,0,0)",
            margin: "8px",
            borderRadius: "8px",
          }}
        />
        <div
          style={{
            width: "140px",
            height: "60px",
            background: "rgba(255,0,0,0.5)",
            margin: "8px",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Example 4 - HSL & HSLA */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ HSL & HSLA</h2>
        <p>
          HSL = Hue (0–360°), Saturation (%), Lightness (%). HSLA adds alpha.
        </p>
        <CodeCopyBlock
          code={`div { background: hsl(200, 100%, 50%); } /* Bright Blue */
div { background: hsla(200, 100%, 50%, 0.4); } /* Transparent Blue */`}
        />
        <div
          style={{
            width: "140px",
            height: "60px",
            background: "hsl(200, 100%, 50%)",
            margin: "8px",
            borderRadius: "8px",
          }}
        />
        <div
          style={{
            width: "140px",
            height: "60px",
            background: "hsla(200, 100%, 50%, 0.4)",
            margin: "8px",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Example 5 - HWB */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ HWB (CSS Color 4)</h2>
        <p>
          HWB = Hue, Whiteness, Blackness. Similar to HSL but easier for
          shading.
        </p>
        <CodeCopyBlock
          code={`div { background: hwb(90 20% 0%); } /* Greenish */`}
        />
        <div
          style={{
            width: "140px",
            height: "60px",
            background: "hwb(90 20% 0%)",
            margin: "8px",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Example 6 - LAB */}
      <div className="tutorial-section">
        <h2 className="section-title">6️⃣ LAB</h2>
        <p>
          LAB is a modern perceptual color space with Lightness + color axes.
        </p>
        <CodeCopyBlock code={`div { background: lab(60 80 -60); }`} />
        <div
          style={{
            width: "140px",
            height: "60px",
            background: "lab(60 80 -60)",
            margin: "8px",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Example 7 - LCH */}
      <div className="tutorial-section">
        <h2 className="section-title">7️⃣ LCH</h2>
        <p>LCH = Lightness, Chroma, Hue. Useful for smooth gradients.</p>
        <CodeCopyBlock code={`div { background: lch(70 80 40); }`} />
        <div
          style={{
            width: "140px",
            height: "60px",
            background: "lch(70 80 40)",
            margin: "8px",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Example 8 - currentColor */}
      <div className="tutorial-section">
        <h2 className="section-title">8️⃣ currentColor</h2>
        <p>
          <code>currentColor</code> uses the element's own <code>color</code>{" "}
          value for other properties.
        </p>
        <CodeCopyBlock
          code={`button {
  color: blue;
  border: 3px solid currentColor; /* same as text color */
}`}
        />
        <button
          style={{
            color: "blue",
            border: "3px solid currentColor",
            fontSize: "20px",
            padding: "8px 14px",
          }}
        >
          Blue Button
        </button>
      </div>

      {/* Example 9 - transparent */}
      <div className="tutorial-section">
        <h2 className="section-title">9️⃣ transparent</h2>
        <p>
          The keyword <code>transparent</code> is equivalent to{" "}
          <code>rgba(0,0,0,0)</code>.
        </p>
        <CodeCopyBlock code={`div { background: transparent; }`} />
        <div
          style={{
            width: "140px",
            height: "60px",
            background: "transparent",
            border: "2px dashed gray",
            margin: "8px",
          }}
        />
      </div>

      {/* Example 10 - Mix with Gradients */}
      <div className="tutorial-section">
        <h2 className="section-title">🔟 Colors in Gradients</h2>
        <p>Color values can be combined in gradients.</p>
        <CodeCopyBlock
          code={`div { 
  background: linear-gradient(to right, red, orange, yellow, green, blue); 
}`}
        />
        <div
          style={{
            width: "100%",
            height: "70px",
            background:
              "linear-gradient(to right, red, orange, yellow, green, blue)",
            borderRadius: "8px",
            margin: "8px 0",
          }}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde047)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#854d0e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>CSS Color Module Level 5</b> for more advanced
            spaces.
          </li>
          <li>
            Explore <code>color-mix()</code> function (modern browsers).
          </li>
          <li>Check color contrast ratios for accessibility.</li>
          <li>Experiment with gradients and filters for creative effects.</li>
        </ul>
      </div>
    </div>
  );
}
